import {
    Column,
    Container,
    DropDown,
    Grid,
    Input,
    Label,
    Row,
    SecondaryButton,
    toPascalCase,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const SearchHeader = ({
    context,
    doQueryPolicies,
    query,
    products,
    reference,
    doReduceQueryElement,
    doGetListPolicies,
}) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();
    const { list } = query;

    const [localFilters, setLocalFilters] = useState({
        brokerCompanyReferenceGuid: list.brokerCompanyReferenceGuid || null,
        brokerReferenceGuid: list.brokerReferenceGuid || null,
        product: list.product || null,
        status: list.status || null,
        policyTransactionType: list.policyTransactionType || null,
        clientFirstName: list.clientFirstName || '',
        clientLastName: list.clientLastName || '',
        clientPhoneFullNumber: list.clientPhoneFullNumber || '',
        clientEmail: list.clientEmail || '',
        referenceNumber: list.referenceNumber || '',
        policyNumber: list.policyNumber || '',
        quoteNumber: list.quoteNumber || '',
        quoteColorTag: list.quoteColorTag || '',
        doingBusinessAs: list.doingBusinessAs || '',
    });

    // Get translated texts for dropdown options
    const allBrokersText = intl.formatMessage({ id: 'Policies_List.search.allBrokers' });
    const allBrokeragesText = intl.formatMessage({ id: 'Policies_List.search.allBrokerages' });
    const allProductsText = intl.formatMessage({ id: 'Policies_List.search.allProducts' });
    const insuredPhoneText = intl.formatMessage({ id: 'Policies_List.search.insuredPhone' });
    const insuredEmailText = intl.formatMessage({ id: 'Policies_List.search.insuredEmail' });
    const referenceNumberText = intl.formatMessage({ id: 'Policies_List.search.referenceNumber' });
    const policyNumberText = intl.formatMessage({ id: 'Policies_List.search.policyNumber' });
    const quoteNumberText = intl.formatMessage({ id: 'Policies_List.search.quoteNumber' });
    const tagText = intl.formatMessage({ id: 'Policies_List.search.tag' });
    const doingBusinessAsText = intl.formatMessage({ id: 'Policies_List.search.doingBusinessAs' });
    const clearText = intl.formatMessage({ id: 'Policies_List.search.clear' });
    const brokerageText = intl.formatMessage({ id: 'Policies_List.table.brokerage' });
    const assignedToText = intl.formatMessage({ id: 'Policies_List.table.assignedTo' });
    const productText = intl.formatMessage({ id: 'Policies_List.table.product' });
    const statusText = intl.formatMessage({ id: 'Policies_List.table.status' });
    const typeText = intl.formatMessage({ id: 'Policies_List.table.type' });

    // Create dropdown options with translated text
    const assignees = [{ code: null, value: allBrokersText }];
    if (reference?.references?.ActiveBrokers) {
        for (const activeBroker of reference.references.ActiveBrokers) {
            assignees.push({ code: activeBroker.code, value: activeBroker.value });
        }
    }

    // Create dropdown options with translated text
    const brokerages = [{ code: null, value: allBrokeragesText }];
    if (reference?.references?.ActiveBrokerCompanies) {
        reference.references.ActiveBrokerCompanies.forEach((activeBrokerage) => {
            brokerages.push({ code: activeBrokerage.code, value: activeBrokerage.value });
        });
    }

    const productsList = [{ code: null, value: allProductsText }];
    if (products) {
        for (const product of products) {
            productsList.push({ code: product.code, value: product.name });
        }
    }

    // Create transaction status dropdown options with translated text
    const allTransactionStatusText = intl.formatMessage({ id: 'Policies_List.search.status.all' });
    const abandonedText = intl.formatMessage({ id: 'Policies_List.search.status.abandoned' });
    const acceptedText = intl.formatMessage({ id: 'Policies_List.search.status.accepted' });
    const bindRequestedText = intl.formatMessage({ id: 'Policies_List.search.status.bindRequested' });
    const boundText = intl.formatMessage({ id: 'Policies_List.search.status.bound' });
    const cancelledText = intl.formatMessage({ id: 'Policies_List.search.status.cancelled' });
    const incompleteText = intl.formatMessage({ id: 'Policies_List.search.status.incomplete' });
    const issuedText = intl.formatMessage({ id: 'Policies_List.search.status.issued' });
    const lostText = intl.formatMessage({ id: 'Policies_List.search.status.lost' });
    const quotedText = intl.formatMessage({ id: 'Policies_List.search.status.quoted' });
    const quoteAcceptedText = intl.formatMessage({ id: 'Policies_List.search.status.quoteAccepted' });
    const quoteIssuedText = intl.formatMessage({ id: 'Policies_List.search.status.quoteIssued' });
    const referredText = intl.formatMessage({ id: 'Policies_List.search.status.referred' });
    const rejectedText = intl.formatMessage({ id: 'Policies_List.search.status.rejected' });
    const submittedText = intl.formatMessage({ id: 'Policies_List.search.status.submitted' });

    const statuses = [
        { code: null, value: allTransactionStatusText },
        { code: 'abandoned', value: abandonedText },
        { code: 'accepted', value: acceptedText },
        { code: 'bindRequested', value: bindRequestedText },
        { code: 'bound', value: boundText },
        { code: 'cancelled', value: cancelledText },
        { code: 'incomplete', value: incompleteText },
        { code: 'issued', value: issuedText },
        { code: 'lost', value: lostText },
        { code: 'quoted', value: quotedText },
        { code: 'quoteAccepted', value: quoteAcceptedText },
        { code: 'quoteIssued', value: quoteIssuedText },
        // { code: 'quoting', value: 'Quoting' }, - Not shown as this is a transient state
        { code: 'referred', value: referredText },
        { code: 'rejected', value: rejectedText },
        { code: 'submitted', value: submittedText },
        // { code: 'underwritingRequired', value: 'Underwriting Required' }, - Not supported yet
    ];

    // Create transaction type dropdown options with translated text
    const allTransactionTypesText = intl.formatMessage({ id: 'Policies_List.search.transactionType.all' });
    const renewalText = intl.formatMessage({ id: 'Policies_List.search.transactionType.renewal' });
    const newBusinessText = intl.formatMessage({ id: 'Policies_List.search.transactionType.newBusiness' });
    const endorsementText = intl.formatMessage({ id: 'Policies_List.search.transactionType.endorsement' });
    const certificateText = intl.formatMessage({ id: 'Policies_List.search.transactionType.certificate' });

    const transactionTypes = [
        { code: null, value: allTransactionTypesText },
        { code: 'renewal', value: renewalText },
        { code: 'new', value: newBusinessText },
        { code: 'endorsement', value: endorsementText },
        { code: 'certificate', value: certificateText },
    ];

    const handleSearchClick = () => {
        for (const key of Object.keys(localFilters)) {
            doReduceQueryElement({ code: key, value: localFilters[key] });
        }
        doReduceQueryElement({ code: 'skip', value: 0 });
        doReduceQueryElement({ code: 'take', value: 25 });
        doQueryPolicies();
    };

    const handleClearClick = () => {
        const clearedFilters = {
            brokerCompanyReferenceGuid: null,
            brokerReferenceGuid: null,
            product: null,
            status: null,
            policyTransactionType: null,
            clientFirstName: '',
            clientLastName: '',
            clientPhoneFullNumber: '',
            clientEmail: '',
            referenceNumber: '',
            policyNumber: '',
            quoteNumber: '',
            quoteColorTag: '',
            doingBusinessAs: '',
        };
        setLocalFilters(clearedFilters);
        for (const key of Object.keys(clearedFilters)) {
            doReduceQueryElement({ code: key, value: clearedFilters[key] });
        }
        doReduceQueryElement({ code: 'skip', value: 0 });
        doReduceQueryElement({ code: 'take', value: 25 });
        doGetListPolicies();
    };

    const handleInputChange = (field, value) => {
        setLocalFilters((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const getFilterText = () => {
        const filterText = [];
        const { list } = query;

        if (list.brokerCompanyReferenceGuid) {
            filterText.push(
                `[b]${brokerageText}[b]: ${brokerages.find((x) => x.code === list.brokerCompanyReferenceGuid).value}`,
            );
        }

        if (list.brokerReferenceGuid) {
            filterText.push(
                `[b]${assignedToText}[b]: ${assignees.find((x) => x.code === list.brokerReferenceGuid).value}`,
            );
        }

        if (list.product) {
            const productName = products.find((product) => product.code === list.product).name;
            filterText.push(`[b]${productText}[b]: ${productName}`);
        }

        if (list.status) {
            filterText.push(`[b]${statusText}[b]: ${toPascalCase(list.status)}`);
        }

        if (list.policyTransactionType) {
            const displayedType = list.policyTransactionType;
            filterText.push(`[b]${typeText}[b]: ${toPascalCase(displayedType)}`);
        }

        if (list.clientFirstName) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_List.search.fname' })}[b]: ${list.clientFirstName}`,
            );
        }

        if (list.clientLastName) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_List.search.lname' })}[b]: ${list.clientLastName}`,
            );
        }

        if (list.clientPhoneFullNumber) {
            filterText.push(`[b]${insuredPhoneText}[b]: ${list.clientPhoneFullNumber}`);
        }

        if (list.clientEmail) {
            filterText.push(`[b]${insuredEmailText}[b]: ${list.clientEmail}`);
        }

        if (list.referenceNumber) {
            filterText.push(`[b]${referenceNumberText}[b]: ${list.referenceNumber}`);
        }

        if (list.policyNumber) {
            filterText.push(`[b]${policyNumberText}[b]: ${list.policyNumber}`);
        }

        if (list.quoteNumber) {
            filterText.push(`[b]${quoteNumberText}[b]: ${list.quoteNumber}`);
        }

        if (list.quoteColorTag) {
            filterText.push(`[b]${tagText}[b]: ${list.quoteColorTag}`);
        }

        if (list.doingBusinessAs) {
            filterText.push(`[b]${doingBusinessAsText}[b]: ${list.doingBusinessAs}`);
        }

        return filterText.join(', ');
    };

    return (
        <Container>
            <Container top={sizes.xs} right={sizes.xs} left={sizes.xs}>
                <Container top={sizes.none} bottom={sizes.xs}>
                    <Grid>
                        <Row>
                            <Column xs={11} nogutter>
                                <Label text={getFilterText()} textAlign={alignments.left} />
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column xs={0} md={0} lg={2} />
                            <Column xs={12} md={12}>
                                <Grid>
                                    <Row nogutter>
                                        <Column xs={6} md={3}>
                                            <DropDown
                                                top={sizes.xs}
                                                placeholder={productText}
                                                value={{ code: localFilters.product }}
                                                references={productsList}
                                                changeEvent={(value) => handleInputChange('product', value.code)}
                                                allowEdit
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <DropDown
                                                top={sizes.xs}
                                                placeholder={statusText}
                                                value={{ code: localFilters.status }}
                                                references={statuses}
                                                changeEvent={(value) => handleInputChange('status', value.code)}
                                                allowEdit
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <DropDown
                                                top={sizes.xs}
                                                placeholder={typeText}
                                                value={{ code: localFilters.policyTransactionType }}
                                                references={transactionTypes}
                                                // biome-ignore lint/suspicious/noEmptyBlockStatements: noop callback for referencesEvent contract
                                                referencesEvent={() => {}}
                                                changeEvent={(value) =>
                                                    handleInputChange('policyTransactionType', value.code)
                                                }
                                                allowEdit
                                            />
                                        </Column>
                                        <Column xs={0} md={3} />
                                        {context.session.userType === 'administrator' && (
                                            <Column xs={6} md={3}>
                                                <DropDown
                                                    top={sizes.xs}
                                                    placeholder={brokerageText}
                                                    value={{ code: localFilters.brokerCompanyReferenceGuid }}
                                                    references={brokerages}
                                                    referencesEvent={() =>
                                                        reference.doGetReferences('ActiveBrokerCompanies')
                                                    }
                                                    changeEvent={(value) =>
                                                        handleInputChange('brokerCompanyReferenceGuid', value.code)
                                                    }
                                                    allowEdit
                                                />
                                            </Column>
                                        )}
                                        <Column xs={6} md={3}>
                                            <DropDown
                                                top={sizes.xs}
                                                placeholder={assignedToText}
                                                value={{ code: localFilters.brokerReferenceGuid }}
                                                references={assignees}
                                                referencesEvent={() => reference.doGetReferences('ActiveBrokers')}
                                                changeEvent={(value) =>
                                                    handleInputChange('brokerReferenceGuid', value.code)
                                                }
                                                allowEdit
                                            />
                                        </Column>
                                        <Column xs={0} md={context.session.userType === 'administrator' ? 6 : 9} />
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={intl.formatMessage({ id: 'Policies_List.search.fname' })}
                                                value={localFilters.clientFirstName}
                                                changeEvent={(value) => handleInputChange('clientFirstName', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={intl.formatMessage({ id: 'Policies_List.search.lname' })}
                                                value={localFilters.clientLastName}
                                                changeEvent={(value) => handleInputChange('clientLastName', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={insuredPhoneText}
                                                value={localFilters.clientPhoneFullNumber}
                                                changeEvent={(value) =>
                                                    handleInputChange('clientPhoneFullNumber', value)
                                                }
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={insuredEmailText}
                                                value={localFilters.clientEmail}
                                                changeEvent={(value) => handleInputChange('clientEmail', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={referenceNumberText}
                                                value={localFilters.referenceNumber}
                                                changeEvent={(value) => handleInputChange('referenceNumber', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={policyNumberText}
                                                value={localFilters.policyNumber}
                                                changeEvent={(value) => handleInputChange('policyNumber', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={quoteNumberText}
                                                value={localFilters.quoteNumber}
                                                changeEvent={(value) => handleInputChange('quoteNumber', value)}
                                            />
                                        </Column>
                                        <Column xs={0} md={3} />
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={tagText}
                                                value={localFilters.quoteColorTag}
                                                changeEvent={(value) => handleInputChange('quoteColorTag', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={3}>
                                            <Input
                                                top={sizes.xs}
                                                inverse
                                                placeholder={doingBusinessAsText}
                                                value={localFilters.doingBusinessAs}
                                                changeEvent={(value) => handleInputChange('doingBusinessAs', value)}
                                            />
                                        </Column>
                                        <Column xs={6} md={12} lg={12}>
                                            <Container
                                                inline
                                                fullSize
                                                top={sizes.xs}
                                                horizontalAlignment={alignments.center}
                                            >
                                                <SecondaryButton
                                                    text={<FormattedMessage id='Policies_List.search.search' />}
                                                    bottom={sizes.xs}
                                                    clickEvent={handleSearchClick}
                                                />
                                                <SecondaryButton
                                                    left={sizes.xs}
                                                    bottom={sizes.xs}
                                                    text={clearText}
                                                    clickEvent={handleClearClick}
                                                />
                                            </Container>
                                        </Column>
                                    </Row>
                                </Grid>
                            </Column>
                            <Column xs={0} md={0} lg={2} />
                        </Row>
                    </Grid>
                </Container>
            </Container>
        </Container>
    );
};

export default SearchHeader;
